import { useAuthStore } from "@/stores/auth";

/**
 * API origin. In production this is set to `https://api.forge.hackercat.it`
 * via the `VITE_API_BASE_URL` build-time env var (configured in Cloudflare
 * Pages project settings). In local dev it falls back to an empty string,
 * which means "same origin as the frontend" — the Vite dev proxy handles
 * the rest.
 */
const API_ORIGIN = (import.meta.env.VITE_API_BASE_URL ?? "").replace(/\/$/, "");

const BASE_URL = `${API_ORIGIN}/api`;

/**
 * Build a full API URL from a path like `/auth/google/start` or
 * `/api/preview/modules/xyz.js` (either form is accepted).
 *
 * Use this when you need a real URL (e.g. `window.location.href = ...`,
 * `<a href>`, or direct `fetch` calls bypassing `apiFetch`, OR when the
 * backend returns a server-relative path that needs the cross-origin host
 * prepended before the browser fetches it).
 */
export function apiUrl(path: string): string {
  // Already an absolute URL — leave it alone
  if (path.startsWith("http://") || path.startsWith("https://")) {
    return path;
  }
  // Already has the /api prefix — just prepend the origin
  if (path.startsWith("/api/") || path === "/api") {
    return `${API_ORIGIN}${path}`;
  }
  // Shorthand — prepend origin + /api/
  const slash = path.startsWith("/") ? "" : "/";
  return `${BASE_URL}${slash}${path}`;
}

/**
 * Build a WebSocket URL for a path like `/ws/session?token=...`.
 * Handles the http→ws / https→wss scheme conversion automatically.
 */
export function wsUrl(path: string): string {
  const slash = path.startsWith("/") ? "" : "/";
  const fullPath = `/api${slash}${path}`;
  if (API_ORIGIN) {
    const wsOrigin = API_ORIGIN
      .replace(/^https:/, "wss:")
      .replace(/^http:/, "ws:");
    return `${wsOrigin}${fullPath}`;
  }
  // Local dev: same host, port 3001
  const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
  return `${protocol}//${window.location.hostname}:3001${fullPath}`;
}

export class ApiError extends Error {
  constructor(
    public status: number,
    public statusText: string,
    public body: unknown,
  ) {
    // Prefer the backend's { "error": "..." } message — it says WHY the
    // request was rejected (e.g. which font family failed validation).
    // Fall back to the bare status line for empty/non-JSON bodies.
    const detail =
      body && typeof body === "object" && "error" in body &&
      typeof (body as { error: unknown }).error === "string"
        ? (body as { error: string }).error
        : `API Error ${status}: ${statusText}`;
    super(detail);
    this.name = "ApiError";
  }
}

export async function apiFetch<T>(
  path: string,
  options: RequestInit = {},
): Promise<T> {
  const { token, logout, activeTeamId } = useAuthStore.getState();

  const headers = new Headers(options.headers);
  if (!headers.has("Content-Type") && options.body) {
    headers.set("Content-Type", "application/json");
  }
  if (token) {
    headers.set("Authorization", `Bearer ${token}`);
  }
  if (activeTeamId) {
    headers.set("X-Team-Id", activeTeamId);
  }

  const res = await fetch(`${BASE_URL}${path}`, { ...options, headers });

  // Clear auth state on 401 — don't redirect (causes unexpected reloads
  // across pages). The Layout component's auth guard will handle navigation
  // naturally on the next render.
  if (res.status === 401 && token) {
    logout();
    throw new ApiError(401, "Unauthorized", null);
  }

  if (!res.ok) {
    const body = await res.json().catch(() => null);
    throw new ApiError(res.status, res.statusText, body);
  }

  if (res.status === 204) return undefined as T;

  return res.json() as Promise<T>;
}

export function buildQueryString(
  params: Record<string, string | number | boolean | undefined>,
): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined) {
      search.set(key, String(value));
    }
  }
  const str = search.toString();
  return str ? `?${str}` : "";
}
